import { Breadcrumb, Layout, Spin, Card, Row, Col, Typography, Tag, Table, Button, Divider, message, Modal, Form, InputNumber, Select, Input, Space, Popconfirm, Descriptions } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { APPBACKGROUND_COLOR } from '../appsettings';
import { CheckCircleOutlined, CloseCircleOutlined, DollarOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import InvoicePDF from './invoice-pdf';
import { PDFDownloadLink } from '@react-pdf/renderer';

const { Title, Text } = Typography;

const ViewInvoice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const USERID = localStorage.getItem("USERID");

    const [loading, setLoading] = useState(true);
    const [invoice, setInvoice] = useState(null);
    const [items, setItems] = useState([]);
    const [payments, setPayments] = useState([]);

    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentForm] = Form.useForm();

    // Fetch invoice data
    const fetchInvoice = async () => {
        setLoading(true);
        try {
            const response = await axiosClient.get(`/invoice/get_invoice_by_id.php?id=${id}`);
            const data = response.data;

            if (data.status === 'success') {
                setInvoice(data.invoice);
                setItems(data.items);
                setPayments(data.payments);
            } else {
                message.error(data.message || 'Failed to load invoice.');
                navigate('/invoices');
            }
        } catch (error) {
            console.error('Error:', error);
            message.error('Failed to load invoice.');
            navigate('/invoices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Get status tag color
    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'green';
            case 'Pending': return 'orange';
            case 'Cancelled': return 'red';
            default: return 'default';
        }
    };

    // Update invoice status
    const updateStatus = async (newStatus) => {
        try {
            const response = await axiosClient.post('/invoice/update_invoice_status.php', {
                inv_id: parseInt(id),
                status: newStatus,
                by: USERID
            });

            if (response.data.status === 'success') {
                message.success(response.data.message);
                fetchInvoice();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error('Failed to update status.');
        }
    };

    // Add payment
    const handleAddPayment = async (values) => {
        setPaymentLoading(true);
        try {
            const response = await axiosClient.post('/invoice/add_payment.php', {
                inv_id: parseInt(id),
                payment_type: values.payment_type,
                payment_ref: values.payment_ref || '',
                paid_amount: values.paid_amount,
                by: USERID
            });

            if (response.data.status === 'success') {
                message.success(response.data.message);
                setPaymentModalVisible(false);
                paymentForm.resetFields();
                fetchInvoice();
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to add payment.');
        } finally {
            setPaymentLoading(false);
        }
    };



    // Item table columns
    const itemColumns = [
        { title: '#', render: (_, __, index) => index + 1, width: 50 },
        { 
            title: 'Item', 
            render: (_, record) => (
                <div>
                    <div><strong>[{record.sp_code}] {record.sp_title}</strong></div>
                    <small style={{ color: '#888' }}>{record.sp_type} • {record.sp_duration}</small>
                </div>
            )
        },
        { 
            title: 'Unit Price', 
            dataIndex: 'unit_price', 
            align: 'right',
            render: (price) => `Rs. ${Number(price).toLocaleString()}`
        },
        { 
            title: 'Sales Price', 
            dataIndex: 'sales_price', 
            align: 'right',
            render: (price) => `Rs. ${Number(price).toLocaleString()}`
        },
        { title: 'Qty', dataIndex: 'order_qty', align: 'center', width: 60 },
        { 
            title: 'Period', 
            render: (_, record) => (
                <div>
                    {dayjs(record.start_date).format('DD/MM/YYYY')} - {dayjs(record.end_date).format('DD/MM/YYYY')}
                </div>
            )
        },
        { 
            title: 'Sub Total', 
            dataIndex: 'sub_total', 
            align: 'right',
            render: (total) => <strong>Rs. {Number(total).toLocaleString()}</strong>
        },
    ];

    // Payment table columns
    const paymentColumns = [
        { title: '#', render: (_, __, index) => index + 1, width: 50 },
        { 
            title: 'Type', 
            dataIndex: 'payment_type',
            render: (type) => (
                <Tag color={type === 'CASH' ? 'green' : type === 'CARD' ? 'blue' : 'purple'}>
                    {type}
                </Tag>
            )
        },
        { title: 'Reference', dataIndex: 'payment_ref', render: (ref) => ref || '-' },
        { 
            title: 'Amount', 
            dataIndex: 'paid_amount', 
            align: 'right',
            render: (amount) => <strong>Rs. {Number(amount).toLocaleString()}</strong>
        },
        { 
            title: 'Date', 
            dataIndex: 'process_dt',
            render: (date) => dayjs(date).format('DD MMM YYYY hh:mm A')
        },
    ];

    if (loading) {
        return (
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />
                <Content style={{ padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spin size="large" tip="Loading Invoice..." />
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    if (!invoice) {
        return null;
    }

    const balance = parseFloat(invoice.tobe_paid_amount) - parseFloat(invoice.total_paid_amount);

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ padding: "10px" }}>
                <Breadcrumb style={{ marginBottom: 10 }}>
                    <Breadcrumb.Item><Link to={"/invoices"}>Invoices</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>Invoice #{invoice.inv_ref}</Breadcrumb.Item>
                </Breadcrumb>

                {/* Header Card */}
                <Card style={{ marginBottom: 15 }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={3} style={{ margin: 0 }}>
                                Invoice #{invoice.inv_ref}
                            </Title>
                            <Text type="secondary">
                                Created: {dayjs(invoice.process_dt).format('DD MMM YYYY hh:mm A')}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <Tag color={getStatusColor(invoice.inv_status)} style={{ fontSize: 16, padding: '5px 15px' }}>
                                    {invoice.inv_status.toUpperCase()}
                                </Tag>
                            </Space>
                        </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Descriptions title="Member Information" column={1} size="small">
                                <Descriptions.Item label="Name">{invoice.member_name}</Descriptions.Item>
                                <Descriptions.Item label="Member ID">{invoice.member_code}</Descriptions.Item>
                                <Descriptions.Item label="Phone">{invoice.member_phone || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Email">{invoice.member_email || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card style={{ background: '#f5f5f5' }}>
                                <Row gutter={[8, 8]}>
                                    <Col span={12}><Text>Sub Total:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text>Rs. {Number(invoice.total_items_sum).toLocaleString()}</Text>
                                    </Col>
                                    <Col span={12}><Text>Discount:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text>- Rs. {Number(invoice.total_discount).toLocaleString()}</Text>
                                    </Col>
                                    <Col span={12}><Text>Tax:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text>+ Rs. {Number(invoice.total_tax).toLocaleString()}</Text>
                                    </Col>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Col span={12}><Text strong>Invoice Total:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text strong style={{ fontSize: 18 }}>
                                            {invoice.invoice_currency} {Number(invoice.invoice_total).toLocaleString()}
                                        </Text>
                                    </Col>
                                    <Col span={12}><Text>Paid:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text style={{ color: '#52c41a' }}>
                                            Rs. {Number(invoice.total_paid_amount).toLocaleString()}
                                        </Text>
                                    </Col>
                                    <Col span={12}><Text strong>Balance:</Text></Col>
                                    <Col span={12} style={{ textAlign: 'right' }}>
                                        <Text strong style={{ fontSize: 18, color: balance > 0 ? '#f5222d' : '#52c41a' }}>
                                            Rs. {balance.toLocaleString()}
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    {invoice.invoice_note && (
                        <>
                            <Divider />
                            <Text type="secondary">Note: {invoice.invoice_note}</Text>
                        </>
                    )}
                </Card>

                {/* Items Table */}
                <Card title="Invoice Items" style={{ marginBottom: 15 }}>
                    <Table
                        columns={itemColumns}
                        dataSource={items}
                        rowKey="inv_item_id"
                        pagination={false}
                        bordered
                        size="small"
                    />
                </Card>

                {/* Payments Table */}
                <Card 
                    title="Payments" 
                    style={{ marginBottom: 15 }}
                    extra={
                        invoice.inv_status === 'Pending' && balance > 0 && (
                            <Button 
                                type="primary" 
                                icon={<DollarOutlined />}
                                onClick={() => setPaymentModalVisible(true)}
                            >
                                Add Payment
                            </Button>
                        )
                    }
                >
                    <Table
                        columns={paymentColumns}
                        dataSource={payments}
                        rowKey="inv_pay_id"
                        pagination={false}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No payments recorded' }}
                    />
                </Card>

                {/* Action Buttons */}
                <Card>
                    <Row justify="space-between">
                        <Col>
                            <Space>
                                <PDFDownloadLink 
                                    document={<InvoicePDF invoice={invoice} items={items} payments={payments} />} 
                                    fileName={`Invoice_${invoice.inv_ref}.pdf`}
                                >
                                    {({ blob, url, loading, error }) => (
                                        <Button icon={<PrinterOutlined />} loading={loading}>
                                            {loading ? 'Generating PDF...' : 'Download PDF'}
                                        </Button>
                                    )}
                                </PDFDownloadLink>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                {invoice.inv_status === 'Pending' && (
                                    <>
                                        <Popconfirm
                                            title="Mark this invoice as completed?"
                                            onConfirm={() => updateStatus('Completed')}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button type="primary" icon={<CheckCircleOutlined />}>
                                                Mark Completed
                                            </Button>
                                        </Popconfirm>
                                        <Popconfirm
                                            title="Cancel this invoice? This action cannot be undone."
                                            onConfirm={() => updateStatus('Cancelled')}
                                            okText="Yes"
                                            cancelText="No"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Button danger icon={<CloseCircleOutlined />}>
                                                Cancel Invoice
                                            </Button>
                                        </Popconfirm>
                                    </>
                                )}
                                <Button onClick={() => navigate('/invoices')}>
                                    Back to List
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Content>
            <Footerdiv />

            {/* Payment Modal */}
            <Modal
                title="Add Payment"
                open={paymentModalVisible}
                onCancel={() => {
                    setPaymentModalVisible(false);
                    paymentForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={paymentForm}
                    layout="vertical"
                    onFinish={handleAddPayment}
                    initialValues={{ payment_type: 'CASH' }}
                >
                    <Form.Item
                        name="paid_amount"
                        label="Amount"
                        rules={[
                            { required: true, message: 'Please enter amount' },
                            { type: 'number', min: 1, message: 'Amount must be greater than 0' },
                            { type: 'number', max: balance, message: `Maximum amount is Rs. ${balance.toLocaleString()}` }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="Enter payment amount"
                            min={1}
                            max={balance}
                            formatter={value => `Rs. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/Rs\.\s?|(,*)/g, '')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="payment_type"
                        label="Payment Type"
                        rules={[{ required: true, message: 'Please select payment type' }]}
                    >
                        <Select>
                            <Select.Option value="CASH">Cash</Select.Option>
                            <Select.Option value="CARD">Card</Select.Option>
                            <Select.Option value="TRANSFER">Bank Transfer</Select.Option>
                            <Select.Option value="OTHER">Other</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="payment_ref"
                        label="Reference (optional)"
                    >
                        <Input placeholder="Transaction ID, Card last 4 digits, etc." />
                    </Form.Item>

                    <Row justify="end" gutter={[10, 10]}>
                        <Col>
                            <Button onClick={() => {
                                setPaymentModalVisible(false);
                                paymentForm.resetFields();
                            }}>
                                Cancel
                            </Button>
                        </Col>
                        <Col>
                            <Button type="primary" htmlType="submit" loading={paymentLoading}>
                                Add Payment
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ViewInvoice;
